import {
  DRAW_COOLDOWN_MS,
  DRAW_COST,
  RARITIES,
  availableDuplicateCount,
  itemCatalog,
  rarityDefinition,
  rollRarity,
  type GameInventoryItem,
  type GameReward,
  type GameState,
} from "./game";

export interface GameClient {
  state(): Promise<GameState>;
  draw(idempotencyKey: string): Promise<GameReward>;
  exchange(itemIds: string[], idempotencyKey: string): Promise<GameReward>;
  reset?(): Promise<GameState>;
  syncEarned?(earnedTokens: number): Promise<GameState>;
}

interface DemoSave {
  earned: number;
  spent: number;
  nextEligibleAt: string | null;
  inventory: Record<string, number>;
  transactions: Record<string, GameReward>;
}

const DEMO_KEY = "cpl-game-demo-v2";

function demoStartingInventory(): Record<string, number> {
  return { C01: 4, C02: 3, C03: 3, C04: 4, U01: 1, R01: 1, E01: 1 };
}

function initialDemo(earned: number): DemoSave {
  return { earned, spent: 0, nextEligibleAt: null, inventory: demoStartingInventory(), transactions: {} };
}

function readDemo(earned: number): DemoSave {
  try {
    const saved = JSON.parse(localStorage.getItem(DEMO_KEY) ?? "null") as DemoSave | null;
    if (saved && Number.isSafeInteger(saved.spent) && saved.inventory && saved.transactions) {
      saved.earned = Math.max(saved.earned, earned);
      return saved;
    }
  } catch { /* start fresh */ }
  return initialDemo(earned);
}

function writeDemo(save: DemoSave): void {
  localStorage.setItem(DEMO_KEY, JSON.stringify(save));
}

function inventoryFrom(save: DemoSave): GameInventoryItem[] {
  return itemCatalog().filter((item) => (save.inventory[item.id] ?? 0) > 0).map((item) => ({ ...item, quantity: save.inventory[item.id] }));
}

function stateFrom(save: DemoSave): GameState {
  const balance = Math.max(0, save.earned - save.spent);
  const cooldownReady = !save.nextEligibleAt || Date.parse(save.nextEligibleAt) <= Date.now();
  return {
    serverTime: new Date().toISOString(),
    wallet: { earned: save.earned, spent: save.spent, balance },
    draw: { cost: DRAW_COST, nextEligibleAt: save.nextEligibleAt, eligible: balance >= DRAW_COST && cooldownReady },
    inventory: inventoryFrom(save),
    demo: true,
  };
}

function randomItem(rarity: GameInventoryItem["rarity"]): GameInventoryItem {
  const candidates = itemCatalog().filter((item) => item.rarity === rarity);
  return candidates[Math.floor(Math.random() * candidates.length)];
}

export function createDemoGameClient(earnedTokens = 1_500_000): GameClient {
  let currentEarnedTokens = earnedTokens;
  return {
    async state() {
      return stateFrom(readDemo(currentEarnedTokens));
    },
    async draw(idempotencyKey) {
      const save = readDemo(currentEarnedTokens);
      if (save.transactions[idempotencyKey]) return save.transactions[idempotencyKey];
      const current = stateFrom(save);
      if (current.wallet.balance < DRAW_COST) throw new Error("剩余 Token 不足");
      if (!current.draw.eligible) throw new Error("下一次抽奖机会尚未恢复");
      const item = randomItem(rollRarity(Math.floor(Math.random() * 10_000)));
      const previous = save.inventory[item.id] ?? 0;
      save.inventory[item.id] = previous + 1;
      save.spent += DRAW_COST;
      save.nextEligibleAt = new Date(Date.now() + DRAW_COOLDOWN_MS).toISOString();
      const reward: GameReward = {
        transactionId: crypto.randomUUID(),
        item: { ...item, quantity: previous + 1 },
        isNew: previous === 0,
        balance: Math.max(0, save.earned - save.spent),
        nextEligibleAt: save.nextEligibleAt,
      };
      save.transactions[idempotencyKey] = reward;
      writeDemo(save);
      return reward;
    },
    async exchange(itemIds, idempotencyKey) {
      const save = readDemo(currentEarnedTokens);
      if (save.transactions[idempotencyKey]) return save.transactions[idempotencyKey];
      if (itemIds.length !== 10) throw new Error("请选择正好10件重复物品");
      const catalog = new Map(itemCatalog().map((item) => [item.id, item]));
      const requested = new Map<string, number>();
      for (const id of itemIds) requested.set(id, (requested.get(id) ?? 0) + 1);
      const selected = itemIds.map((id) => ({ ...catalog.get(id)!, quantity: save.inventory[id] ?? 0 }));
      if (selected.some((item) => !item.id)) throw new Error("包含未知物品");
      const source = selected[0].rarity;
      if (selected.some((item) => item.rarity !== source)) throw new Error("只能兑换同一品质物品");
      const target = rarityDefinition(source).next;
      if (!target) throw new Error("传说品质不能兑换");
      for (const [id, quantity] of requested) {
        const owned = { ...catalog.get(id)!, quantity: save.inventory[id] ?? 0 };
        if (quantity > availableDuplicateCount(owned)) throw new Error(`${owned.label}的重复数量不足`);
      }
      const item = randomItem(target);
      const previous = save.inventory[item.id] ?? 0;
      for (const [id, quantity] of requested) save.inventory[id] -= quantity;
      save.inventory[item.id] = previous + 1;
      const reward: GameReward = {
        transactionId: crypto.randomUUID(),
        item: { ...item, quantity: previous + 1 },
        isNew: previous === 0,
        balance: Math.max(0, save.earned - save.spent),
        nextEligibleAt: save.nextEligibleAt,
      };
      save.transactions[idempotencyKey] = reward;
      writeDemo(save);
      return reward;
    },
    async reset() {
      const save = initialDemo(currentEarnedTokens);
      writeDemo(save);
      return stateFrom(save);
    },
    async syncEarned(nextEarnedTokens) {
      currentEarnedTokens = Math.max(currentEarnedTokens, Math.max(0, Math.floor(nextEarnedTokens)));
      return stateFrom(readDemo(currentEarnedTokens));
    },
  };
}

async function responseJson<T>(response: Response): Promise<T> {
  const body = await response.json() as T & { error?: string };
  if (!response.ok) throw new Error(body.error || `请求失败 (${response.status})`);
  return body;
}

export function createRemoteGameClient(): GameClient {
  return {
    async state() {
      return responseJson<GameState>(await fetch("/api/game/state", { credentials: "include" }));
    },
    async draw(idempotencyKey) {
      return responseJson<GameReward>(await fetch("/api/game/draw", {
        method: "POST", credentials: "include", headers: { "content-type": "application/json", "idempotency-key": idempotencyKey }, body: "{}",
      }));
    },
    async exchange(itemIds, idempotencyKey) {
      return responseJson<GameReward>(await fetch("/api/game/exchange", {
        method: "POST", credentials: "include", headers: { "content-type": "application/json", "idempotency-key": idempotencyKey }, body: JSON.stringify({ itemIds }),
      }));
    },
  };
}

export function approvedProbabilityLabel(): string {
  return RARITIES.map((rarity) => `${rarity.label} ${(rarity.probabilityBps / 100).toFixed(2)}%`).join(" · ");
}
